import { useState } from 'react';
import {
  useIntegrations,
  useMailFolders,
  useMailsInFolder,
} from '../o365/hooks';
import type { M365Email, M365MailFolder } from '../o365/api';
import styles from './DashboardEmailTab.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const DAYS_OPTIONS = [
  { label: '1 Tag',    value: 1  },
  { label: '7 Tage',   value: 7  },
  { label: '14 Tage',  value: 14 },
  { label: 'Alle',     value: 0  },
] as const;

const FOLDER_NAME_PRIORITY: Record<string, number> = {
  'posteingang':          0,
  'inbox':                0,
  'gesendete elemente':   1,
  'sent items':           1,
  'gesendet':             1,
  'entwürfe':             2,
  'drafts':               2,
  'archiv':               3,
  'archive':              3,
  'junk-e-mail':          4,
  'junk email':           4,
  'spam':                 4,
  'gelöschte elemente':   5,
  'deleted items':        5,
  'papierkorb':           5,
  'outbox':               6,
  'ausgang':              6,
};

function isInboxFolder(f: M365MailFolder): boolean {
  const name = f.displayName.toLowerCase();
  return name === 'posteingang' || name === 'inbox' || f.wellKnownName === 'inbox';
}

function sortFolders(folders: M365MailFolder[]): M365MailFolder[] {
  return [...folders].sort((a, b) => {
    const pa = FOLDER_NAME_PRIORITY[a.displayName.toLowerCase()] ?? 99;
    const pb = FOLDER_NAME_PRIORITY[b.displayName.toLowerCase()] ?? 99;
    if (pa !== pb) return pa - pb;
    return a.displayName.localeCompare(b.displayName, 'de');
  });
}

function formatEmailDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

// ── EmailCard ─────────────────────────────────────────────────────────────────

function EmailCard({ email, onClick }: { email: M365Email; onClick: () => void }) {
  const displayName = email.from?.emailAddress?.name || email.from?.emailAddress?.address || '—';

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${styles.emailCard} ${!email.isRead ? styles.emailCardUnread : ''}`}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className={styles.emailCardInner}>
        <div className={styles.emailHeader}>
          <span className={styles.emailSubject}>
            {!email.isRead && <span className={styles.unreadDot} aria-hidden="true" />}
            {email.subject || '(Kein Betreff)'}
          </span>
          <span className={styles.emailDate}>
            {formatEmailDate(email.receivedDateTime)}
          </span>
        </div>
        <div className={styles.emailMeta}>
          <span className={styles.emailFrom}>{displayName}</span>
          {email.hasAttachments && (
            <span className={styles.attachBadge} aria-label="Hat Anhang">📎</span>
          )}
        </div>
        {email.bodyPreview && (
          <p className={styles.emailPreview}>{email.bodyPreview}</p>
        )}
      </div>
    </div>
  );
}

// ── EmailDetailModal ──────────────────────────────────────────────────────────

function EmailDetailModal({ email, onClose }: { email: M365Email; onClose: () => void }) {
  const senderEmail = email.from?.emailAddress?.address ?? null;
  const senderName  = email.from?.emailAddress?.name ?? '';

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.detailModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.detailHeader}>
          <h2 className={styles.detailSubject}>
            {email.subject || '(Kein Betreff)'}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className={styles.detailMeta}>
          <div className={styles.detailMetaRow}>
            <span className={styles.detailMetaLabel}>Von</span>
            <span className={styles.detailMetaValue}>
              {senderName || senderEmail || '—'}
              {senderName && senderEmail && (
                <span className={styles.detailSenderEmail}> &lt;{senderEmail}&gt;</span>
              )}
            </span>
          </div>
          <div className={styles.detailMetaRow}>
            <span className={styles.detailMetaLabel}>Datum</span>
            <span className={styles.detailMetaValue}>
              {new Date(email.receivedDateTime).toLocaleString('de-DE', {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          {email.hasAttachments && (
            <div className={styles.detailMetaRow}>
              <span className={styles.detailMetaLabel}>Anhang</span>
              <span className={styles.detailMetaValue}>📎 Vorhanden</span>
            </div>
          )}
        </div>

        <div className={styles.detailBody}>
          {email.body?.contentType === 'html' && email.body.content ? (
            <iframe
              srcDoc={`<!DOCTYPE html><html><head><style>body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;font-size:14px;color:#333;margin:0;padding:12px;line-height:1.5;word-break:break-word;}a{color:#2563eb;}img{max-width:100%;height:auto;}</style></head><body>${email.body.content}</body></html>`}
              className={styles.detailBodyIframe}
              sandbox="allow-same-origin"
              title="E-Mail Inhalt"
            />
          ) : email.bodyPreview ? (
            email.bodyPreview
          ) : (
            <em className={styles.detailBodyEmpty}>Kein Vorschautext verfügbar.</em>
          )}
        </div>

        <div className={styles.detailFooter}>
          <a
            href={email.webLink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.outlookBtn}
          >
            ↗ In Outlook öffnen
          </a>
        </div>
      </div>
    </div>
  );
}

// ── DashboardEmailTab ─────────────────────────────────────────────────────────

export function DashboardEmailTab() {
  const { data: integrationsData, isLoading: integrationsLoading } = useIntegrations();
  const isConnected =
    integrationsData?.data?.some(
      (i) => i.provider === 'MICROSOFT_365' && i.connected,
    ) ?? false;

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [days, setDays] = useState<number>(7);
  const [detailEmail, setDetailEmail] = useState<M365Email | null>(null);

  const { data: foldersData, isLoading: foldersLoading, isError: foldersError } =
    useMailFolders();
  const folders = (foldersData?.data ?? []) as M365MailFolder[];
  const sortedFolders = sortFolders(folders);

  const inboxFolder = sortedFolders.find(isInboxFolder);
  const activeFolderId = selectedFolderId ?? inboxFolder?.id ?? 'inbox';

  const { data: emailsData, isLoading: emailsLoading, error: emailsError } =
    useMailsInFolder(activeFolderId, days);
  const emails = (emailsData?.data ?? []) as M365Email[];

  if (integrationsLoading) {
    return <p className={styles.status}>Verbindung wird geprüft…</p>;
  }

  if (!isConnected) {
    return (
      <div className={styles.notConnected}>
        <span className={styles.notConnectedIcon}>📭</span>
        <p className={styles.notConnectedTitle}>Microsoft 365 nicht verbunden</p>
        <p className={styles.notConnectedSub}>
          Verbinden Sie Ihr Konto in Ihrem Profil, um E-Mails anzuzeigen.
        </p>
      </div>
    );
  }

  const unreadCount = emails.filter((e) => !e.isRead).length;

  return (
    <div className={styles.root}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageHeaderTitle}>E-Mail</h1>
          {unreadCount > 0 && (
            <span className={styles.pageHeaderCount}>{unreadCount} ungelesen</span>
          )}
        </div>
      </div>

      <div className={styles.filterBar}>
        <span className={styles.filterLabel}>Zeitraum:</span>
        {DAYS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`${styles.filterBtn} ${days === opt.value ? styles.filterBtnActive : ''}`}
            onClick={() => setDays(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className={styles.layout}>
        <aside className={styles.folderTree}>
          {foldersLoading && (
            <p className={styles.status}>Ordner laden…</p>
          )}
          {foldersError && !foldersLoading && (
            <p className={styles.errorText} style={{ padding: '0.5rem 0.875rem', fontSize: '0.8125rem' }}>
              Ordner konnten nicht geladen werden.
            </p>
          )}
          {!foldersLoading && !foldersError && sortedFolders.length === 0 && (
            <p className={styles.status} style={{ padding: '0.5rem 0.875rem', fontSize: '0.8125rem' }}>
              Keine Ordner gefunden.
            </p>
          )}
          {!foldersLoading && sortedFolders.length > 0 && (
            <ul className={styles.folderList}>
              {sortedFolders.map((folder) => (
                <li key={folder.id}>
                  <button
                    type="button"
                    className={`${styles.folderItem} ${
                      activeFolderId === folder.id ||
                      (activeFolderId === 'inbox' && isInboxFolder(folder))
                        ? styles.folderItemActive
                        : ''
                    }`}
                    onClick={() => setSelectedFolderId(folder.id)}
                  >
                    <span className={styles.folderName}>{folder.displayName}</span>
                    {folder.unreadItemCount > 0 && (
                      <span className={styles.folderBadge}>
                        {folder.unreadItemCount}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className={styles.emailList}>
          {emailsLoading && (
            <p className={styles.status}>E-Mails werden geladen…</p>
          )}
          {emailsError && (
            <p className={styles.errorText}>
              E-Mails konnten nicht geladen werden.
            </p>
          )}
          {!emailsLoading && !emailsError && emails.length === 0 && (
            <p className={styles.status}>
              Keine E-Mails im gewählten Zeitraum.
            </p>
          )}
          {emails.map((email) => (
            <EmailCard
              key={email.id}
              email={email}
              onClick={() => setDetailEmail(email)}
            />
          ))}
        </section>
      </div>

      {detailEmail && (
        <EmailDetailModal
          email={detailEmail}
          onClose={() => setDetailEmail(null)}
        />
      )}
    </div>
  );
}
